import * as React from 'react';
import { useDialogContext, DialogOverlay, Dialog } from '@wartek-id/dialog';
import { createContext } from '@wartek-id/react-utils';
import { Primitive } from '@wartek-id/react-primitive';
import clsx from 'clsx';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var _a;
var SheetContextProvider = (_a = createContext(), _a[0]), useSheetContext = _a[1];

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Sheet-module_sheetHead__1nmPo{font-weight:var(--font-weight-body);font-size:var(--size-font-body);line-height:var(--size-line-height-text-body);color:var(--color-text-default);display:flex;align-items:center;width:100%;height:54px;min-height:54px;padding:0 16px;margin:0}.Sheet-module_sheetHead__1nmPo.Sheet-module_bordered__1vsCO{box-shadow:0 2px 4px rgba(0,0,0,.05)}.Sheet-module_sheetHead__1nmPo.Sheet-module_bold__3ay1B{font-weight:var(--font-weight-heading-sm);font-size:var(--size-font-heading-sm);line-height:var(--size-line-height-text-heading-sm)}.Sheet-module_sheetContent__suEOU{z-index:101;background-color:#fff;box-shadow:0 0 38px -10px hsl(206 22% 7%/35%),0 0 35px -15px hsl(206 22% 7%/20%);position:fixed;width:100vw;max-height:100vh;will-change:transform}.Sheet-module_sheetContent__suEOU .Sheet-module_body__2noMc{display:flex;flex-direction:column}.Sheet-module_sheetContent__suEOU[data-state=entered]{-webkit-animation:Sheet-module_slideIn__3lTAp .3s cubic-bezier(.22,1,.36,1) forwards;animation:Sheet-module_slideIn__3lTAp .3s cubic-bezier(.22,1,.36,1) forwards}.Sheet-module_sheetContent__suEOU[data-state=exited],.Sheet-module_sheetContent__suEOU[data-state=exiting]{-webkit-animation:Sheet-module_slideOut__1G-sP .3s cubic-bezier(.22,1,.36,1) forwards;animation:Sheet-module_slideOut__1G-sP .3s cubic-bezier(.22,1,.36,1) forwards}.Sheet-module_sheetContent__suEOU.Sheet-module_bottom__1MF59{transform:translateY(100%)}.Sheet-module_sheetContent__suEOU.Sheet-module_bottom__1MF59.Sheet-module_entered__2CWHX{transform:translateY(0)}.Sheet-module_sheetContent__suEOU.Sheet-module_top__2skil{transform:translateY(-100%)}.Sheet-module_sheetContent__suEOU.Sheet-module_top__2skil.Sheet-module_entered__2CWHX{transform:translateY(0)}.Sheet-module_sheetContent__suEOU.Sheet-module_left__f8yLq{transform:translateX(-100%)}.Sheet-module_sheetContent__suEOU.Sheet-module_left__f8yLq.Sheet-module_entered__2CWHX{transform:translateX(0)}.Sheet-module_sheetContent__suEOU.Sheet-module_right__3DRmo{transform:translateX(100%)}.Sheet-module_sheetContent__suEOU.Sheet-module_right__3DRmo.Sheet-module_entered__2CWHX{transform:translateX(0)}.Sheet-module_sheetCloseButton__2DhpQ{background:none;display:flex;border:none;position:absolute;padding:0;right:12px;top:8px;cursor:pointer}.Sheet-module_left__f8yLq{--transform-value:translate3d(-100%,0,0);top:0;left:0;bottom:0}.Sheet-module_bottom__1MF59{--transform-value:translate3d(0,100%,0);border-radius:12px 12px 0 0;left:0;right:0;bottom:0}.Sheet-module_top__2skil{--transform-value:translate3d(0,-100%,0);border-radius:0 0 12px 12px;left:0;right:0;top:0}.Sheet-module_right__3DRmo{--transform-value:translate3d(100%,0,0);top:0;right:0;bottom:0}.Sheet-module_sm__mQjjK{height:20vh}.Sheet-module_md__bHJJ5{height:40vh}.Sheet-module_lg__iImmQ{height:60vh}.Sheet-module_xl__es96F{height:80vh}.Sheet-module_full__MKLA7{height:100vh}@-webkit-keyframes Sheet-module_slideIn__3lTAp{0%{transform:var(--transform-value)}to{transform:translateZ(0)}}@keyframes Sheet-module_slideIn__3lTAp{0%{transform:var(--transform-value)}to{transform:translateZ(0)}}@-webkit-keyframes Sheet-module_slideOut__1G-sP{0%{transform:translateZ(0)}to{transform:var(--transform-value)}}@keyframes Sheet-module_slideOut__1G-sP{0%{transform:translateZ(0)}to{transform:var(--transform-value)}}@-webkit-keyframes Sheet-module_fadeIn__yIj_K{0%{opacity:0}to{opacity:1}}@keyframes Sheet-module_fadeIn__yIj_K{0%{opacity:0}to{opacity:1}}@-webkit-keyframes Sheet-module_fadeOut__30Uov{0%{opacity:1}to{opacity:0}}@keyframes Sheet-module_fadeOut__30Uov{0%{opacity:1}to{opacity:0}}";
var styles = {"sheetHead":"Sheet-module_sheetHead__1nmPo","bordered":"Sheet-module_bordered__1vsCO","bold":"Sheet-module_bold__3ay1B","sheetContent":"Sheet-module_sheetContent__suEOU","body":"Sheet-module_body__2noMc","slideIn":"Sheet-module_slideIn__3lTAp","slideOut":"Sheet-module_slideOut__1G-sP","bottom":"Sheet-module_bottom__1MF59","entered":"Sheet-module_entered__2CWHX","top":"Sheet-module_top__2skil","left":"Sheet-module_left__f8yLq","right":"Sheet-module_right__3DRmo","sheetCloseButton":"Sheet-module_sheetCloseButton__2DhpQ","sm":"Sheet-module_sm__mQjjK","md":"Sheet-module_md__bHJJ5","lg":"Sheet-module_lg__iImmQ","xl":"Sheet-module_xl__es96F","full":"Sheet-module_full__MKLA7","fadeIn":"Sheet-module_fadeIn__yIj_K","fadeOut":"Sheet-module_fadeOut__30Uov"};
styleInject(css_248z);

var DESCRIPTION_NAME$1 = 'SheetCloseButton';
var DESCRIPTION_DEFAULT_TAG$1 = 'button';
var SheetCloseButton = React.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? DESCRIPTION_DEFAULT_TAG$1 : _a; props.className; var buttonProps = __rest(props, ["as", "className"]);
    var onClose = useDialogContext().onClose;
    return (React.createElement(Primitive, __assign({ "aria-label": "Close", ref: ref, as: as, className: styles.sheetCloseButton, onClick: function () { return onClose(); } }, buttonProps),
        React.createElement("svg", { width: "38", height: "38", viewBox: "0 0 38 38", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M25.8653 13.9574C26.2607 13.5677 26.2629 12.9306 25.8704 12.5381L25.4616 12.1292C25.069 11.7367 24.432 11.739 24.0423 12.1343L19.7115 16.5278C19.3198 16.9252 18.6788 16.9252 18.2872 16.5278L13.9564 12.1343C13.5667 11.739 12.9296 11.7367 12.5371 12.1292L12.1282 12.5381C11.7357 12.9306 11.738 13.5677 12.1333 13.9574L16.5269 18.2881C16.9242 18.6798 16.9242 19.3208 16.5269 19.7125L12.1333 24.0433C11.738 24.4329 11.7357 25.07 12.1282 25.4625L12.5371 25.8714C12.9296 26.2639 13.5667 26.2616 13.9564 25.8663L18.2872 21.4728C18.6788 21.0754 19.3198 21.0754 19.7115 21.4728L24.0423 25.8663C24.432 26.2616 25.069 26.2639 25.4616 25.8714L25.8704 25.4625C26.2629 25.07 26.2607 24.4329 25.8653 24.0433L21.4718 19.7125C21.0745 19.3208 21.0745 18.6798 21.4718 18.2881L25.8653 13.9574Z", fill: "#45474A" }))));
});
SheetCloseButton.displayName = DESCRIPTION_NAME$1;

var SheetOverlay = function (_a) {
    var closeOnOverlayClick = _a.closeOnOverlayClick, props = __rest(_a, ["closeOnOverlayClick"]);
    var type = useSheetContext().type;
    return (React.createElement(DialogOverlay, __assign({ closeOnOverlayClick: type === 'alert' ? false : closeOnOverlayClick }, props)));
};

var sizesValue = {
    sm: 20,
    md: 40,
    lg: 60,
    xl: 80,
    full: 100,
};
var generateSize = function (size, position) {
    if (size === 'auto')
        return;
    if (position === 'top' || position === 'bottom') {
        return {
            height: sizesValue[size] + 'vh',
        };
    }
    return {
        width: sizesValue[size] + 'vw',
    };
};
var generateMaxHeight = function (size) {
    if (size === 'auto')
        return { maxHeight: '100vh' };
    return {
        maxHeight: sizesValue[size] + 'vh',
    };
};
var SheetContent = function (_a) {
    var children = _a.children, transitionState = _a.transitionState, className = _a.className, props = __rest(_a, ["children", "transitionState", "className"]);
    var onClose = useDialogContext().onClose;
    var _b = useSheetContext(), position = _b.position, sheetSize = _b.sheetSize, type = _b.type;
    var contentClasses = clsx(styles.sheetContent, styles[position], transitionState, position, className);
    var handleKeyDown = function (event) {
        if (event.key === 'Escape') {
            onClose();
        }
    };
    return (React.createElement("div", __assign({ className: contentClasses, "data-state": transitionState, role: type === 'modal' ? 'dialog' : 'alertdialog', "aria-modal": "true", "aria-labelledby": "sheet-title", "aria-describedby": "sheet-description", style: generateSize(sheetSize, position), onKeyDown: handleKeyDown }, props),
        React.createElement("div", { id: "sheet-content", className: styles.body, style: generateMaxHeight(sheetSize) }, React.Children.map(children, function (child) {
            return React.cloneElement(child, { style: { overflow: 'auto' } });
        }))));
};

var DESCRIPTION_NAME = 'SheetTitle';
var DESCRIPTION_DEFAULT_TAG = 'h2';
var SheetTitle = React.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? DESCRIPTION_DEFAULT_TAG : _a, className = props.className, bordered = props.bordered, bold = props.bold, cancelProps = __rest(props, ["as", "className", "bordered", "bold"]);
    return (React.createElement(Primitive, __assign({ ref: ref, as: as, className: clsx(styles.sheetHead, bordered && styles.bordered, bold && styles.bold, className) }, cancelProps)));
});
SheetTitle.displayName = DESCRIPTION_NAME;

var Sheet = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    var isOpen = props.isOpen, onClose = props.onClose, _b = props.animationDuration, animationDuration = _b === void 0 ? { enter: 0, exit: 300 } : _b, _c = props.sheetSize, sheetSize = _c === void 0 ? 'auto' : _c, _d = props.position, position = _d === void 0 ? 'bottom' : _d, _e = props.autoFocus, autoFocus = _e === void 0 ? true : _e, _f = props.returnFocus, returnFocus = _f === void 0 ? true : _f, _g = props.type, type = _g === void 0 ? 'modal' : _g;
    return (React.createElement(SheetContextProvider, { value: { sheetSize: sheetSize, position: position, type: type } },
        React.createElement(Dialog, { isOpen: isOpen, onClose: onClose, animationDuration: animationDuration, autoFocus: autoFocus, returnFocus: returnFocus }, children)));
};

export { Sheet, SheetCloseButton, SheetContent, SheetOverlay, SheetTitle };
